import { CountryCodeToLabelMap } from '../../../../../../../../../../shared/helpers/country-region-helpers';
import { DeclaredUnit } from '../../../../../../../../../../shared/interfaces/IComponentDetails';
import {
  ICalculationResponse,
  IProcessDetails,
} from '../../../../../../../../../../shared/interfaces/IFootprint';
import {
  ENERGY_SOURCE,
  EnergySourceEmissionFactor,
  IInputMaterial,
  PROCESS_DROPDOWN_OPTIONS,
  ProcessIdsMap,
} from '../../PcfWizard.type';

type MaterialEmissionFactor = {
  value?: number;
  declaredUnit?: DeclaredUnit;
  /** The declared amount of `declaredUnit` the value refers to, e.g. 5 in "7 kg CO₂e / 5 kg". */
  perAmount: number;
};

export type MaterialsTableRow = {
  key: string;
  /** The waste treatment of the material above it. */
  isWaste: boolean;
  name?: string;
  /** Always in kg. */
  amount?: number | null;
  emissionFactor: MaterialEmissionFactor;
  /** The factor as declared, shown next to a factor that was converted to per kg. */
  declaredEmissionFactor?: MaterialEmissionFactor;
  source?: string;
  emissions?: number;
};

/**
 * The amount the declared emission factor refers to, in kg when the material has a product mass.
 * Same normalisation as the backend uses for the input material's emissions.
 */
const getNormalizeFactor = (material: IInputMaterial): number => {
  return material.product_mass_per_declared_unit ?? material.unitary_product_amount ?? 1;
};

/**
 * Emissions of producing a material, without treating its waste: (amount + waste weight) x
 * normalised emission factor, since the wasted share has to be produced as well. The backend's
 * `emissions` is never used.
 */
export const getMaterialEmissions = (material: IInputMaterial): number | undefined => {
  const normalizeFactor = getNormalizeFactor(material);
  const pcf = material.pcf_excluding_biogenic;

  if (pcf == null || normalizeFactor <= 0 || material.amount_per_piece == null) {
    return undefined;
  }

  return (material.amount_per_piece + (material.waste_per_piece ?? 0)) * (pcf / normalizeFactor);
};

/**
 * Rows of the materials table of an Individual Process: every input material, directly followed by
 * the row of its own waste treatment.
 */
export const getMaterialsTableRows = (materials?: IInputMaterial[]): MaterialsTableRow[] => {
  return (materials ?? []).filter(Boolean).flatMap((material, index) => {
    const wasteTreatment = material.waste_treatment;
    const normalizeFactor = getNormalizeFactor(material);
    const pcf = material.pcf_excluding_biogenic;
    // Amount x emission factor of the waste row.
    const wasteEmissions =
      wasteTreatment && material.waste_per_piece != null
        ? material.waste_per_piece * wasteTreatment.pcfExcludingBiogenic
        : undefined;

    const materialRow: MaterialsTableRow = {
      key: `material-${index}`,
      isWaste: false,
      name: material.material_name,
      // Material plus its waste, since both have to be produced. The waste row below shows the
      // waste alone.
      amount:
        material.amount_per_piece == null
          ? material.amount_per_piece
          : material.amount_per_piece + (material.waste_per_piece ?? 0),
      // Always per kg: the backend normalises the declared factor and multiplies it with a kg amount.
      emissionFactor: {
        value: pcf == null || normalizeFactor <= 0 ? undefined : pcf / normalizeFactor,
        declaredUnit: DeclaredUnit.kilogram,
        perAmount: 1,
      },
      // The declared factor refers to the normalize factor in kg, e.g. "7 kg CO₂e / 5 kg". Only
      // shown when it differs from the per kg factor above and is made of valid values.
      declaredEmissionFactor:
        pcf == null || normalizeFactor <= 0 || normalizeFactor === 1
          ? undefined
          : { value: pcf, declaredUnit: DeclaredUnit.kilogram, perAmount: normalizeFactor },
      source: material.source,
      emissions: getMaterialEmissions(material),
    };

    if (!wasteTreatment) {
      return [materialRow];
    }

    const wasteRow: MaterialsTableRow = {
      key: `waste-${index}`,
      isWaste: true,
      name: wasteTreatment.name,
      amount: material.waste_per_piece,
      emissionFactor: {
        value: wasteTreatment.pcfExcludingBiogenic,
        declaredUnit: DeclaredUnit.kilogram,
        perAmount: 1,
      },
      source: wasteTreatment.source,
      emissions: wasteEmissions,
    };

    return [materialRow, wasteRow];
  });
};

/** Sum of the emissions of all rows of the materials table, or undefined when no row has a value. */
export const getMaterialsTotalEmissions = (rows: MaterialsTableRow[]): number | undefined => {
  if (
    rows.every((row) => {
      return row.emissions == null;
    })
  ) {
    return undefined;
  }

  return rows.reduce((sum, row) => {
    return sum + (row.emissions ?? 0);
  }, 0);
};

// Plain names: the unit is shown next to the value wherever these are used.
export const ENERGY_SOURCE_LABEL_KEYS: Record<ENERGY_SOURCE, string> = {
  [ENERGY_SOURCE.ELECTRICITY]: 'calculationDetailsPage.sections.energy.sources.electricity',
  [ENERGY_SOURCE.COMPRESSED_AIR]: 'calculationDetailsPage.sections.energy.sources.compressedAir',
  [ENERGY_SOURCE.GAS]: 'calculationDetailsPage.sections.energy.sources.gas',
  [ENERGY_SOURCE.FUEL]: 'calculationDetailsPage.sections.energy.sources.fuel',
};

export type EnergyTableRow = {
  key: string;
  source: ENERGY_SOURCE;
  amount?: number | null;
  /** In kg CO₂e per unit of the source (kWh or m³). */
  emissionFactor?: number;
  dataSource?: string;
  emissions?: number;
};

// The electricity factor is stored in grams, every other source's in kilograms.
const GRAMS_PER_KILOGRAM = 1000;

const toKilogramEmissionFactor = (source: ENERGY_SOURCE, factor?: number): number | undefined => {
  return factor != null && source === ENERGY_SOURCE.ELECTRICITY
    ? factor / GRAMS_PER_KILOGRAM
    : factor;
};

/**
 * Electricity is named after its country and year, or after its custom tariff when it has no
 * country. The other energy sources have no such name.
 */
const getElectricityName = (details?: EnergySourceEmissionFactor): string | undefined => {
  if (details?.countryCode) {
    const country = CountryCodeToLabelMap[details.countryCode] ?? details.countryCode;

    return [country, details.year].filter(Boolean).join(' | ');
  }

  return details?.name || undefined;
};

/**
 * Rows of the energy table of an Individual Process, one per energy source of its machine, in the
 * order they were entered. The backend only reports the machine's total, so a row's emissions are
 * its amount times its emission factor; together they add up to that total.
 */
export const getEnergyTableRows = (machines?: IProcessDetails['machines']): EnergyTableRow[] => {
  return (machines ?? []).flatMap((machine, machineIndex) => {
    return (machine?.energy_sources ?? []).flatMap((energySource, index) => {
      const source = energySource?.source;

      if (!source) {
        return [];
      }

      const details = energySource.energy_source_details;
      const isElectricity = source === ENERGY_SOURCE.ELECTRICITY;

      const emissionFactor = toKilogramEmissionFactor(source, details?.emissionFactor);

      return [
        {
          key: `${machineIndex}-${index}`,
          source,
          amount: energySource.amount,
          emissionFactor,
          dataSource: isElectricity ? getElectricityName(details) : details?.source,
          emissions:
            energySource.amount != null && emissionFactor != null
              ? energySource.amount * emissionFactor
              : undefined,
        },
      ];
    });
  });
};

export type EnergySourceOverviewRow = {
  source: ENERGY_SOURCE;
  /** As stored: grams CO₂e for electricity, kilograms for every other source. */
  emissionFactor?: number;
  /** The factor in kg CO₂e, only set where it is stored in another unit (electricity). */
  convertedEmissionFactor?: number;
  /** The name of the electricity tariff: its custom name, or its country and year. */
  customName?: string;
  dataSource?: string;
};

/**
 * One row per energy source used by the Individual Processes of a calculation. A source used in
 * several processes appears once, since its emission factor and source are the same wherever it
 * is used. Rows follow the order of the energy source dropdown.
 */
export const getEnergySourceOverviewRows = (
  processes?: IProcessDetails[],
): EnergySourceOverviewRow[] => {
  const rowsBySource = new Map<ENERGY_SOURCE, EnergySourceOverviewRow>();

  processes?.forEach((process) => {
    if (process?.process_id !== ProcessIdsMap[PROCESS_DROPDOWN_OPTIONS.INDIVIDUAL_PROCESS]) {
      return;
    }

    process.machines?.forEach((machine) => {
      machine?.energy_sources?.forEach((energySource) => {
        const source = energySource?.source;

        if (!source || rowsBySource.has(source)) {
          return;
        }

        const details = energySource.energy_source_details;
        const isElectricity = source === ENERGY_SOURCE.ELECTRICITY;

        rowsBySource.set(source, {
          source,
          emissionFactor: details?.emissionFactor,
          convertedEmissionFactor: isElectricity
            ? toKilogramEmissionFactor(source, details?.emissionFactor)
            : undefined,
          // For the other sources the name is just the source's own key, so it is not shown.
          customName: isElectricity ? getElectricityName(details) : undefined,
          dataSource: details?.source,
        });
      });
    });
  });

  return Object.values(ENERGY_SOURCE).flatMap((source) => {
    const row = rowsBySource.get(source);

    return row ? [row] : [];
  });
};

export const calculateEmissionBreakdownValues = (
  calculationResponse?: ICalculationResponse,
): number[] => {
  let materialInputs = 0;
  let energyConsumption = 0;
  let transportation = 0;
  let wasteTreatment = 0;

  calculationResponse?.data.processes?.forEach((process) => {
    process?.input_materials?.forEach((material: IInputMaterial) => {
      materialInputs += getMaterialEmissions(material) || 0;

      // Individual process: waste is per material (1-1 with waste_treatment)
      wasteTreatment +=
        (material?.waste_per_piece ?? 0) * (material?.waste_treatment?.pcfExcludingBiogenic ?? 0);
    });

    energyConsumption += process?.total_machine_emissions || 0;
    transportation += process?.route_calculation?.total_emissions || 0;

    // Injection molding: process-level scrap/sprue waste
    wasteTreatment += process?.waste_management?.emissions || 0;
  });

  return [materialInputs, energyConsumption, transportation, wasteTreatment];
};
